import type {
	Bucket,
	CommonUrlOptions,
	NormalizedUrlParts,
	NormalizeUrlOptions,
	UrlFrequencyGroup,
	UrlFrequencyOptions,
} from './types';

/**
 * Safely parses a string into a `URL` object.
 *
 * Attempts to construct a `URL` from the provided `urlString`. If the string is not a valid URL,
 * the function returns `null` instead of throwing an error.
 *
 * @param urlString - The string to parse as a URL.
 * @returns The parsed `URL` object if successful, or `null` if parsing fails.
 */
function safeParse(urlString: string): URL | null {
	try {
		return new URL(urlString);
	} catch {
		return null;
	}
}

/**
 * Normalizes the query string of a given URL by optionally stripping specified parameters
 * and sorting the remaining parameters by key and value.
 *
 * @param url - The URL object whose query string will be normalized.
 * @param strip - A set of parameter names (case-insensitive) to remove from the query string.
 * @param normalizeOrder - If true, sorts the query parameters by key and value.
 * @returns The normalized query string, including the leading '?', or an empty string if there are no parameters.
 */
function normalizeQuery(url: URL, strip: Set<string>, normalizeOrder: boolean): string {
	if (!url?.search) {
		return '';
	}
	const params = new URLSearchParams(url.search);
	if (strip.size) {
		for (const key of Array.from(params.keys())) {
			if (strip.has(key.toLowerCase())) {
				params.delete(key);
			}
		}
	}
	if (normalizeOrder) {
		const pairs: [string, string][] = [];
		params.forEach((value, key) => pairs.push([key, value]));
		pairs.sort((a, b) => (a[0] === b[0] ? a[1].localeCompare(b[1]) : a[0].localeCompare(b[0])));
		const sorted = new URLSearchParams();
		for (const [key, value] of pairs) {
			sorted.append(key, value);
		}
		const queryString = sorted.toString();
		return queryString ? `?${queryString}` : '';
	}
	const queryString = params.toString();
	return queryString ? `?${queryString}` : '';
}

/**
 * Normalizes a given URL string by parsing its components, optionally stripping specified query parameters,
 * normalizing the order of query parameters, and handling invalid URLs.
 *
 * @param urlString - The URL string to normalize.
 * @param options - Optional configuration for normalization.
 * @param options.stripQueryParams - An array of query parameter names to remove from the URL.
 * @param options.normalizeQueryOrder - Whether to sort query parameters alphabetically. Defaults to true.
 * @param options.ignoreInvalid - If true, returns `null` instead of throwing an error for invalid URLs.
 * @returns A `NormalizedUrlParts` object containing the normalized URL components, or `null` if the URL is invalid and `ignoreInvalid` is set.
 * @throws {Error} If the URL is invalid and `ignoreInvalid` is not set.
 */
function normalizeUrl(
	urlString: string,
	{
		stripQueryParams = [],
		normalizeQueryOrder = true,
		ignoreInvalid = false,
	}: NormalizeUrlOptions = {},
): NormalizedUrlParts | null {
	const url = safeParse(urlString);
	if (!url) {
		if (ignoreInvalid) {
			return null;
		}
		throw new Error(`Invalid URL: ${urlString}`);
	}
	let pathname = url.pathname;
	if (pathname !== '/' && pathname.endsWith('/')) {
		pathname = pathname.slice(0, -1);
	}
	const search = normalizeQuery(
		url,
		new Set(stripQueryParams.map((s) => s.toLowerCase())),
		normalizeQueryOrder,
	);
	const hash = url.hash;
	const segments = pathname.split('/').filter(Boolean);
	return {
		origin: url.origin,
		pathname,
		search,
		hash,
		segments,
		original: urlString,
	};
}

/**
 * Finds the longest common URL prefix among a list of URLs.
 *
 * This function normalizes the input URLs and determines the shared origin and path prefix.
 * It can optionally include identical query strings and fragments if all URLs share them.
 * Invalid URLs can be ignored, and query parameters can be stripped or reordered for comparison.
 *
 * @param urlStrings - An array of URL strings to compare.
 * @param options - Configuration options for normalization and comparison.
 * @param options.includeIdenticalQuery - If true, includes the query string in the result only if all URLs share the same query string. Defaults to false.
 * @param options.includeIdenticalFragment - If true, includes the fragment in the result only if all URLs share the same fragment. Defaults to false.
 * @param options.ignoreInvalid - If true, ignores invalid URLs in the input array. Defaults to false.
 * @param options.stripQueryParams - An array of query parameter names to remove before comparison. Defaults to an empty array.
 * @param options.normalizeQueryOrder - If true, normalizes the order of query parameters for comparison. Defaults to true.
 * @returns The longest common URL prefix as a string, or an empty string if no common prefix exists.
 */
function findCommonUrlPrefix(urlStrings: string[], options: CommonUrlOptions = {}): string {
	const {
		includeIdenticalQuery = false,
		includeIdenticalFragment = false,
		ignoreInvalid = false,
		stripQueryParams = [],
		normalizeQueryOrder = true,
	} = options;

	if (!urlStrings.length) {
		return '';
	}

	const urlPartsList: NormalizedUrlParts[] = [];
	for (const urlString of urlStrings) {
		const urlParts = normalizeUrl(urlString, {
			stripQueryParams,
			normalizeQueryOrder,
			ignoreInvalid,
		});
		if (urlParts) {
			urlPartsList.push(urlParts);
		}
	}
	if (!urlPartsList.length) {
		return '';
	}

	const firstOrigin = urlPartsList[0].origin;
	if (!urlPartsList.every((urlPart) => urlPart.origin === firstOrigin)) {
		return '';
	}

	const firstSegments = urlPartsList[0].segments;
	let index = 0;
	outer: for (; index < firstSegments.length; index++) {
		for (let k = 1; k < urlPartsList.length; k++) {
			if (urlPartsList[k].segments[index] !== firstSegments[index]) {
				break outer;
			}
		}
	}
	const commonSegments = firstSegments.slice(0, index);
	let pathPart = commonSegments.length ? '/' + commonSegments.join('/') : '';

	// Optionally keep trailing slash (rarely necessary)
	if (
		pathPart &&
		urlPartsList.every(
			(n) => n.segments.length === commonSegments.length && n.pathname.endsWith('/'),
		)
	) {
		pathPart += '/';
	}

	const query =
		includeIdenticalQuery &&
		urlPartsList.every((urlPart) => urlPart.search === urlPartsList[0].search)
			? urlPartsList[0].search
			: '';

	const hash =
		includeIdenticalFragment &&
		urlPartsList.every((urlPart) => urlPart.hash === urlPartsList[0].hash)
			? urlPartsList[0].hash
			: '';

	return `${firstOrigin}${pathPart}${query}${hash}`;
}

/**
 * Groups an array of URLs by their frequency using exact-path grouping.
 * Each distinct origin+pathname becomes its own bucket; singleton bases optionally include
 * normalized query & fragment (subject to stripping/ordering). Multi-member groups (same path)
 * derive a representative base via common prefix logic (allowing optional identical query/fragment).
 *
 * @param urls - Raw URL strings.
 * @param options - Normalization & grouping options.
 * @returns Sorted frequency groups (count desc, then base asc).
 */
export function groupUrlsByFrequency(
	urls: string[],
	options: UrlFrequencyOptions = {},
): UrlFrequencyGroup[] {
	const {
		truncateSingletonFullUrl = false,
		includeIdenticalQuery = false,
		includeIdenticalFragment = false,
		ignoreInvalid = false,
		stripQueryParams = [],
		normalizeQueryOrder = true,
	} = options;

	if (!urls.length) {
		return [];
	}

	// Normalize all URLs
	const urlPartsList: NormalizedUrlParts[] = [];
	for (const urlString of urls) {
		const urlParts = normalizeUrl(urlString, {
			stripQueryParams,
			normalizeQueryOrder,
			ignoreInvalid,
		});
		if (urlParts) {
			urlPartsList.push(urlParts);
		}
	}

	if (!urlPartsList.length) {
		return [];
	}

	const bucketsMap = new Map<string, Bucket>();
	for (const urlParts of urlPartsList) {
		const key = `${urlParts.origin}${urlParts.pathname}`;
		const existing = bucketsMap.get(key);
		if (existing) {
			existing.members.push(urlParts);
		} else {
			bucketsMap.set(key, { key, members: [urlParts] });
		}
	}
	const buckets = Array.from(bucketsMap.values());

	// Finalize groups
	const groups: UrlFrequencyGroup[] = buckets.map((bucket) => {
		const members = bucket.members.map((m) => m.original);
		const count = members.length;

		let base: string;
		if (count === 1) {
			if (truncateSingletonFullUrl) {
				base = `${bucket.members[0].origin}${bucket.members[0].pathname}`;
			} else {
				const urlParts = bucket.members[0];
				base = `${urlParts.origin}${urlParts.pathname}${urlParts.search}${urlParts.hash}`;
			}
		} else {
			base = findCommonUrlPrefix(members, {
				includeIdenticalQuery,
				includeIdenticalFragment,
				ignoreInvalid: true,
				stripQueryParams,
				normalizeQueryOrder,
			});
			if (!base) {
				base = bucket.members[0].origin;
			}
		}
		return { base, count, members };
	});

	// Consolidate identical bases (edge case: strategy could produce different buckets yielding same prefix)
	const consolidated = new Map<string, UrlFrequencyGroup>();
	for (const group of groups) {
		const existing = consolidated.get(group.base);
		if (existing) {
			existing.count += group.count;
			existing.members.push(...group.members);
		} else {
			consolidated.set(group.base, { ...group });
		}
	}

	const finalGroups = Array.from(consolidated.values());
	finalGroups.sort((a, b) => {
		if (b.count !== a.count) {
			return b.count - a.count;
		}
		return a.base.localeCompare(b.base);
	});
	return finalGroups;
}
